/**
 * InfinitSales - ExportContactsModal
 *
 * Modal de exportação de contatos do funil (CSV/XLSX), aberto pelo botão
 * "Exportar" do Kanban. Nasceu do teste com o cliente em 26/08: a 1ª versão
 * era um dropdown que (a) não deixava filtrar como o drawer do inbox, (b)
 * ficava atrás do overlay dos funis e (c) exportava o arquivo "completo",
 * que confundiu o cliente.
 *
 * Regras:
 * - renderiza via portal em document.body com overlay próprio (nada de brigar
 *   com z-index de coluna/funil);
 * - mesmos filtros do drawer do inbox que fazem sentido aqui: etapa, busca
 *   por nome/telefone e campo personalizado (o MESMO CustomFieldFilterFields
 *   do drawer — um componente só para as duas telas);
 * - saída padrão é a LISTA DE LIGAÇÃO (nome, telefone, funil, etapa,
 *   responsável); o arquivo completo (tags, datas, campos) é opção explícita.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Loader2, X } from 'lucide-react';
import CustomFieldFilterFields from '../contacts/CustomFieldFilterFields';

const ExportContactsModal = ({
  isOpen,
  onClose,
  colors,
  funnelName = '',
  labels = [],                 // etapas do funil ativo [{id, name}]
  customFields = [],           // definições p/ o filtro (mesmas do drawer)
  initialCustomFieldValues = [], // filtro já ativo na tela entra pré-carregado
  onExport,                    // async ({bucket, q, detail, format, customFieldValues}) => {rows}
}) => {
  const [scopeAll, setScopeAll] = useState(false); // true = todos os funis
  const [bucket, setBucket] = useState('');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState('basico');
  const [format, setFormat] = useState('csv');
  const [cfValues, setCfValues] = useState([]);
  // Data de ENTRADA do lead. Pergunta do cliente myCar 01/09: "puxar apenas as
  // conversas ou numeros que entraram mes passado". O CRM ja guarda a data de
  // entrada nativa (quando o contato nasceu); o que faltava era filtrar por
  // ela. Nao usa campo personalizado de propósito: o campo que a conta tinha
  // era preenchido por automacao do CRM antigo e parou de ser preenchido.
  const [entradaDe, setEntradaDe] = useState('');
  const [entradaAte, setEntradaAte] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);   // {rows} da última exportação
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setScopeAll(false);
      setBucket('');
      setQ('');
      setDetail('basico');
      setFormat('csv');
      setCfValues(initialCustomFieldValues || []);
      setEntradaDe('');
      setEntradaAte('');
      setDone(null);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const rodar = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setDone(null);
    try {
      const res = await onExport({
        allFunnels: scopeAll,
        bucket: scopeAll ? null : (bucket || null),
        q: q.trim() || null, detail, format,
        customFieldValues: cfValues,
        createdFrom: entradaDe || null,
        createdTo: entradaAte || null,
      });
      setDone({ rows: res?.rows ?? null });
    } catch (err) {
      setError(err?.response?.status === 403
        ? 'Exportação não habilitada para esta conta.'
        : 'Não foi possível exportar. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  const s = {
    overlay: {
      position: 'fixed', inset: 0, zIndex: 1000,
      backgroundColor: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    },
    modal: {
      width: 420, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto',
      borderRadius: 14, padding: 20,
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      boxShadow: '0 18px 48px rgba(0,0,0,0.30)',
      display: 'flex', flexDirection: 'column', gap: 14,
    },
    title: { fontSize: 16, fontWeight: 700, color: colors.textPrimary },
    label: { fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 6 },
    input: {
      width: '100%', padding: '9px 11px', borderRadius: 8, fontSize: 13,
      border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgSecondary, color: colors.textPrimary,
      outline: 'none',
    },
    radioRow: { display: 'flex', flexDirection: 'column', gap: 6 },
    radio: (on) => ({
      display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 11px',
      borderRadius: 8, cursor: 'pointer', fontSize: 13,
      border: `1px solid ${on ? colors.accentPrimary : colors.borderColor}`,
      backgroundColor: on ? `${colors.accentPrimary}12` : 'transparent',
      color: colors.textPrimary,
    }),
    hint: { fontSize: 11, color: colors.textSecondary, marginTop: 2 },
    footer: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
    btn: (primary) => ({
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
      cursor: busy ? 'wait' : 'pointer',
      border: primary ? 'none' : `1px solid ${colors.borderColor}`,
      backgroundColor: primary ? colors.accentPrimary : 'transparent',
      color: primary ? '#fff' : colors.textSecondary,
    }),
  };

  return createPortal(
    <div style={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={s.title}>Exportar contatos · {funnelName}</span>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary }}>
            <X size={18} />
          </button>
        </div>

        <div>
          <div style={s.label}>O que exportar</div>
          <select
            value={scopeAll ? 'all' : 'funnel'}
            onChange={(e) => { setScopeAll(e.target.value === 'all'); setBucket(''); }}
            style={s.input}
          >
            <option value="funnel">Funil atual ({funnelName})</option>
            <option value="all">Todos os funis (todos os contatos com etapa)</option>
          </select>
        </div>

        {!scopeAll && (
          <div>
            <div style={s.label}>Etapa</div>
            <select value={bucket} onChange={(e) => setBucket(e.target.value)} style={s.input}>
              <option value="">Todas as etapas do funil</option>
              {labels.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <div style={s.label}>Busca (nome ou telefone)</div>
          <input
            style={s.input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ex.: Civic, 3499, Maria..."
          />
        </div>

        <div>
          <div style={s.label}>Entrou no CRM entre</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="date" style={{ ...s.input, flex: 1 }} value={entradaDe}
              max={entradaAte || undefined}
              onChange={(e) => setEntradaDe(e.target.value)} />
            <span style={{ fontSize: 12, color: colors.textSecondary }}>e</span>
            <input type="date" style={{ ...s.input, flex: 1 }} value={entradaAte}
              min={entradaDe || undefined}
              onChange={(e) => setEntradaAte(e.target.value)} />
          </div>
          <div style={s.hint}>
            Deixe em branco para exportar sem limite de data. As duas datas entram.
          </div>
        </div>

        {customFields.length > 0 && (
          <div>
            <div style={s.label}>Campos personalizados</div>
            <CustomFieldFilterFields
              fields={customFields}
              values={cfValues}
              onChange={setCfValues}
              colors={colors}
            />
          </div>
        )}

        <div>
          <div style={s.label}>Colunas</div>
          <div style={s.radioRow}>
            <label style={s.radio(detail === 'basico')}>
              <input type="radio" checked={detail === 'basico'} onChange={() => setDetail('basico')} />
              <span>
                Lista de ligação
                <div style={s.hint}>nome, telefone, funil, etapa, responsável</div>
              </span>
            </label>
            <label style={s.radio(detail === 'completo')}>
              <input type="radio" checked={detail === 'completo'} onChange={() => setDetail('completo')} />
              <span>
                Completa
                <div style={s.hint}>+ canal, tags, datas e todos os campos personalizados</div>
              </span>
            </label>
          </div>
        </div>

        <div>
          <div style={s.label}>Formato</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['csv', 'CSV'], ['xlsx', 'Excel (XLSX)']].map(([fmt, rot]) => (
              <label key={fmt} style={{ ...s.radio(format === fmt), flex: 1, justifyContent: 'center' }}>
                <input type="radio" checked={format === fmt} onChange={() => setFormat(fmt)} />
                {rot}
              </label>
            ))}
          </div>
        </div>

        {error && (
          <div style={{ fontSize: 12, color: '#e5484d' }}>{error}</div>
        )}
        {done && (
          <div style={{ fontSize: 12, color: colors.accentPrimary }}>
            Arquivo baixado{done.rows != null ? ` com ${done.rows} contato(s)` : ''}.
          </div>
        )}

        <div style={s.footer}>
          <button type="button" style={s.btn(false)} onClick={onClose}>Fechar</button>
          <button type="button" style={s.btn(true)} onClick={rodar} disabled={busy}>
            {busy
              ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
              : <Download size={15} />}
            Exportar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ExportContactsModal;
